import React, { useState, useMemo } from 'react';
import { CARD_MODE, GRID_MODE, OPERATORS, TYPES } from '../../../../common/list-utils';
import { DefaultPalette } from '@fluentui/react/lib/Styling';
import { ListColumnFilter } from '../../../Common/list-entities/ListColumnFilter';
import TimeTaxSupportSystemClient from '../../../../system';
import { IComboBoxOption, ICommandBarItemProps } from '@fluentui/react';
import { buttonStylesForProjects, defaultCommandBarButtonStyles } from '../../../../styles/button-styles';
import { routes } from '../../../../common/constants';
import { useNavigate } from 'react-router-dom';
import ViewWrapperWithCommandBar from '../../../Layout/ViewWrapperWithCommandBar';
import { useTranslation } from 'react-i18next';
import MessageCourier from '../../../../lib/MessageCourier';
import { DirectSettlementsGridList } from './List/DirectSettlementsGridList';
import useSystemCall from '../../../../common/custom-hooks/useSystemCall';
import Loading from '../../../Spinners/Loading';
import useAgrupadorCorporacionLiquidadoraList from '../../../../shared/hooks/useAgrupadorCorporacionLiquidadoraList';
import { DateCustom } from '../../../../lib/DateCustom';
import { LiquidacionAsincronaEstado } from '../../../../system/liquidacion_asincrona/LiquidacionAsincronaV2System';

interface IDirectSettlementsListAje {
  system: TimeTaxSupportSystemClient;
  changeBlockUI: (state: boolean) => void;
  courier: MessageCourier;
}
export const DirectSettlementsListAje = (props: IDirectSettlementsListAje) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(GRID_MODE);
  const agrupadoresCorporacionLiquidadora = useAgrupadorCorporacionLiquidadoraList(props.system);

  const tipos_de_liquidacion: IComboBoxOption[] = React.useMemo(() => {
    return agrupadoresCorporacionLiquidadora.data.map((a) => ({ key: a.agrcorliq_id, text: a.agrcorliq_nombre }));
  }, [agrupadoresCorporacionLiquidadora]);

  const navigate = useNavigate();

  const baseUrl = React.useMemo(() => {
    return props.system.getLiquidacionAsincronaV2System().getBaseUrlForDatagridDirect();
  }, [props]);

  const onItemClick = (item: any) => {
    props.courier.messageError('');

    // Si el estado esta [REGISTRADO, PROCESANDO, REPROCESAR] no se puede ingresar a la liquidacion porque esta en proceso de ejecucion
    if ([LiquidacionAsincronaEstado.REGISTRADO, LiquidacionAsincronaEstado.PROCESANDO, LiquidacionAsincronaEstado.REPROCESAR].includes(item.lidasinc_estado)) {
      props.courier.messageError(t('La liquidación seleccionada está en proceso de ejecución.'));
      return;
    }

    navigate(`${routes.direct_settlement_aje}/${item.lidasinc_id}`);
  };

  const commonContext = useSystemCall(props.system, (system) => system.getActivitySystem().getCommonContext());

  const collaboratorList = React.useMemo(() => {
    let ret: IComboBoxOption[] = [];
    if (commonContext) {
      ret = commonContext.colaboradores.map((col) => {
        return {
          key: col.getId(),
          text: col.fullName(),
        };
      });
    }
    return ret;
  }, [commonContext]);

  const estadosOptions: IComboBoxOption[] = React.useMemo(() => {
    return [
      { key: LiquidacionAsincronaEstado.REGISTRADO, text: t('Registrado') },
      { key: LiquidacionAsincronaEstado.PENDIENTE, text: t('Pendiente') },
      { key: LiquidacionAsincronaEstado.PRELIQUIDADO, text: t('Preliquidado') },
      { key: LiquidacionAsincronaEstado.ACEPTADO, text: t('Aceptado') },
      { key: LiquidacionAsincronaEstado.ELIMINADO, text: t('Eliminado') },
      { key: LiquidacionAsincronaEstado.REPROCESAR, text: t('Reprocesar') },
      { key: LiquidacionAsincronaEstado.PROCESANDO, text: t('Procesando') },
    ];
  }, []);

  const quickFilters: ListColumnFilter[] = useMemo(
    () => [
      {
        quick: true,
        name: 'lidasinc_id',
        type: TYPES.INPUT,
        placeholder: t('ID'),
        operator: OPERATORS.EQUAL,
      },
      {
        quick: true,
        name: 'lidasinc_tipo_liquidacion',
        type: TYPES.COMBOBOXTEXT,
        placeholder: t('Tipo de liquidacion'),
        operator: OPERATORS.CONTAINS,
        contextForCombobox: tipos_de_liquidacion,
      },
      {
        quick: true,
        name: 'lidasinc_periodo',
        type: TYPES.TYPE_DATE_YEAR_MONTH,
        placeholder: t('Periodo'),
        operator: OPERATORS.YEAR_MONTH_EQUAL_FORMAT_YYYYMM,
        calendarProps: { isDayPickerVisible: false },
        isMonthSelector: true,
        formatDate: DateCustom.formatDateMMYYYY,
      },
      {
        quick: true,
        name: 'lidasinc_estado',
        type: TYPES.COMBOBOXTEXT,
        placeholder: t('Estado'),
        operator: OPERATORS.EQUAL,
        contextForCombobox: estadosOptions,
      },
      {
        quick: true,
        name: 'lidasinc_activo',
        type: TYPES.CHECKBOX,
        labelForCheckbox: t('Activos'),
        checkBoxValue: '1',
        operator: OPERATORS.EQUAL,
      },
      {
        quick: false,
        name: 'lidasinc_fecha_creacion',
        type: TYPES.DATE,
        placeholder: t('Generadas despues del'),
        operator: OPERATORS.AFTER,
      },
      {
        quick: false,
        name: 'lidasinc_col_nombres_completos',
        type: TYPES.COMBOBOXTEXT,
        placeholder: t('Cargado por'),
        operator: OPERATORS.EQUAL,
        contextForCombobox: collaboratorList || [],
      },
    ],
    [tipos_de_liquidacion, collaboratorList, estadosOptions],
  );

  const itemsMenuCalendar = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'add',
        text: t('Nuevo'),
        iconProps: { iconName: 'Add', color: DefaultPalette.themeLighterAlt },
        buttonStyles: buttonStylesForProjects,
        onClick: () => navigate(`${routes.directSettlementView_aje}`),
      },
    ],
    [navigate],
  );

  if (agrupadoresCorporacionLiquidadora.loading) {
    return <Loading title="Cargando" description="Por favor espere estamos procesando la información necesaria para completar su solicitud." />;
  }

  return (
    <ViewWrapperWithCommandBar
      withBorderBottom
      title={''}
      items={itemsMenuCalendar}
      overflowButtonStyles={defaultCommandBarButtonStyles}
      variant="standard"
    >
      <>
        <DirectSettlementsGridList
          changeBlockUI={props.changeBlockUI}
          baseUrl={baseUrl}
          quickFilters={quickFilters}
          onClickCardMode={() => setMode(CARD_MODE)}
          onClickGridMode={() => setMode(GRID_MODE)}
          mode={mode}
          keyList={'liquidaciones_directas'}
          onItemClick={onItemClick}
          courier={props.courier}
        ></DirectSettlementsGridList>
      </>
    </ViewWrapperWithCommandBar>
  );
};
